/**
 * Tenant theme tokens (EN-5154), lifted verbatim from the Retool Tenant
 * Details page's `defaultStyles` state. These are the portal's tenant-theme CSS
 * variables (`applyColorsPatch` consumes the saved object at login), so the
 * names are a contract with the running portal — do not rename them here.
 */
export const DEFAULT_TENANT_STYLES: Readonly<Record<string, string>> = {
  "navbar-link-selected-underline": "#B5DADB",
  "navbar-link-hover-underline": "#E6F3F3",
  "navbar-link-focus-selected-underline": "#B5DADB",
  "navbar-link-focus-border": "#BDB4DD",
  "navbar-search-bar-active-border": "#9182C6",
  "navbar-button-active-border": "#E6F3F3",
  "navbar-ward-select-option-selected-border": "#7562A8",
  "navbar-ward-select-option-selected-hover": "#7562A8",
  "navbar-ward-select-active-text": "#48397D",
  "navbar-ward-select-active-background": "#E9E6F4",
  "navbar-ward-select-active-icon": "#48397D",
  "navbar-ward-select-active-hover": "#eae6f4cc",
  "navbar-ward-select-option-selected-text": "#48397D",
  "table-row-hover-background": "#E9E6F47A",
  "table-row-selected-background": "#E6F3F3",
  "table-row-focus-background": "#E6F3F3",
  "table-row-focus-border": "#BDB4DD",
  "table-row-messages-circle": "#3B787B",
  "table-row-highlighted-background-primary": "#e6f3f3b3",
  "table-row-highlighted-background-secondary": "#e6f3f37a",
  "button-background": "#7562A8",
  "button-text": "#FFFFFF",
  "button-border": "#7562A8",
  "button-hover-background": "#615492",
  "button-hover-border": "#615492",
  "button-focus-border": "#BDB4DD",
  "button-loading-background": "#44396C",
  "button-loading-border": "#44396C",
  "button-loading-hover-background": "#44396C",
  "button-loading-hover-border": "#44396C",
  "button-active-background": "#44396C",
  "button-disabled-text": "#FFFFFF",
  "builder-banner-background": "#4D4279",
  "builder-section-button-selected-underline": "#BDB4DD",
  "builder-section-button-hover-underline": "#E9E6F4",
  "builder-section-button-focus-underline": "#E9E6F4",
  "builder-button-primary-background": "#9182C6",
  "builder-button-primary-border": "#9182C6",
  "builder-button-primary-hover-background": "#BDB4DD",
  "builder-button-primary-focus-border": "#BDB4DD",
  "builder-button-primary-text": "#FFFFFF",
  "builder-button-secondary-background": "#FFFFFF",
  "builder-button-secondary-border": "#FFFFFF",
  "builder-button-secondary-text": "#5D508A",
  "builder-button-secondary-hover-background": "#ECECEC",
  "builder-button-secondary-focus-border": "#BDB4DD",
  "builder-button-tertiary-background": "#00000000",
  "builder-button-tertiary-text": "#FFFFFF",
  "builder-button-tertiary-hover-background": "#FFFFFF26",
  "builder-button-tertiary-focus-border": "#FFFFFF",
  "builder-protocol-action-container-active-bar": "#54A6AA",
  "portal-modal-note-background": "#e9e6f499",
  "portal-modal-note-text": "#5D508A",
  "measurement-modal-filter-button-active-background": "#E9E6F4",
  "measurement-modal-filter-button-active-border": "#BDB4DD",
  "measurement-modal-filter-button-active-text": "#48397D",
  "measurement-modal-filter-button-active-hover-background": "#D0CAE7",
  "measurement-modal-baseline-save-button-text": "#5D508A",
  "measurement-modal-baseline-confirm-modal-cancel-button-hover": "#9182C6",
  "portal-rich-text-editor-toolbar-focus-icon": "#9182C6",
  "portal-rich-text-editor-toolbar-focus-border": "#9182C6",
  "portal-rich-text-editor-toolbar-active-icon": "#9182C6",
  "portal-rich-text-editor-toolbar-active-background": "#E9E6F4",
  "portal-toggle-checked-background": "#BDB4DD",
  "portal-toggle-checked-thumb-background": "#7562A8",
  "portal-toggle-checked-thumb-border": "#7562A8",
  "care-info-add-link-button-color": "#7562A8",
  "care-info-add-link-button-hover-color": "#5D508A",
  "portal-date-picker-popup-today-cell-border": "#BDB4DD",
  "portal-date-picker-popup-time-cell-selected-background": "#BDB4DD",
  "portal-date-picker-popup-calendar-cell-selected-background": "#E9E6F4",
  "portal-date-picker-popup-ok-button-color": "#7562A8",
  "portal-date-picker-popup-ok-button-hover-color": "#615492",
  "portal-time-picker-hover-border": "#BDB4DD",
  "portal-time-picker-focus-border": "#BDB4DD",
  "portal-time-picker-popup-selected-cell": "#E9E6F4",
  "portal-date-range-day-hover-border": "#84C1C3",
  "portal-date-range-day-today-text": "#54A6AA",
  "portal-date-range-day-selected-background": "#54A6AA",
  "portal-date-range-day-today-background": "#E6F3F3",
  "portal-date-range-trail": "#E6F3F3",
  "portal-dropdown-popup-selected-background": "#E9E6F4",
  "portal-dropdown-popup-selected-icon": "#7562A8",
  "portal-dropdown-selected-border": "#BDB4DD",
  "portal-text-input-border-hover": "#BDB4DD",
  "portal-text-input-border-focus": "#BDB4DD",
  "patient-details-edit-hover": "#e9e6f47a",
  "patient-details-edit-dropdown-hover": "#BDB4DD",
  "portal-radio-button-dot": "#7562A8",
  "portal-loading-state-color": "#7562A8",
  "portal-small-add-button-hover": "#7562A8",
  "surveys-progress-bar-stroke-color": "#BDB4DD",
  "surveys-progress-bar-trail-color": "#E9E6F4",
  "surveys-slider-trail": "#B5DADB",
  "surveys-slider-dot": "#9182C6",
};

export interface ITenantStyleGroup {
  id: string;
  label: string;
  tokens: string[];
}

/**
 * Editor grouping. Retool laid the pickers out next to a mock of the portal
 * area they affect; the groups here are those areas.
 */
export const TENANT_STYLE_GROUPS: ITenantStyleGroup[] = [
  {
    id: "navbar",
    label: "Navbar",
    tokens: [
      "navbar-link-selected-underline",
      "navbar-link-hover-underline",
      "navbar-link-focus-selected-underline",
      "navbar-link-focus-border",
      "navbar-search-bar-active-border",
      "navbar-button-active-border",
      "navbar-ward-select-option-selected-border",
      "navbar-ward-select-option-selected-hover",
      "navbar-ward-select-active-text",
      "navbar-ward-select-active-background",
      "navbar-ward-select-active-icon",
      "navbar-ward-select-active-hover",
      "navbar-ward-select-option-selected-text",
    ],
  },
  {
    id: "table",
    label: "Patient table",
    tokens: [
      "table-row-hover-background",
      "table-row-selected-background",
      "table-row-focus-background",
      "table-row-focus-border",
      "table-row-messages-circle",
      "table-row-highlighted-background-primary",
      "table-row-highlighted-background-secondary",
    ],
  },
  {
    id: "buttons",
    label: "Buttons",
    tokens: [
      "button-background",
      "button-text",
      "button-border",
      "button-hover-background",
      "button-hover-border",
      "button-focus-border",
      "button-loading-background",
      "button-loading-border",
      "button-loading-hover-background",
      "button-loading-hover-border",
      "button-active-background",
      "button-disabled-text",
    ],
  },
  {
    id: "builder",
    label: "Protocol & survey builder",
    tokens: [
      "builder-banner-background",
      "builder-section-button-selected-underline",
      "builder-section-button-hover-underline",
      "builder-section-button-focus-underline",
      "builder-button-primary-background",
      "builder-button-primary-border",
      "builder-button-primary-hover-background",
      "builder-button-primary-focus-border",
      "builder-button-primary-text",
      "builder-button-secondary-background",
      "builder-button-secondary-border",
      "builder-button-secondary-text",
      "builder-button-secondary-hover-background",
      "builder-button-secondary-focus-border",
      "builder-button-tertiary-background",
      "builder-button-tertiary-text",
      "builder-button-tertiary-hover-background",
      "builder-button-tertiary-focus-border",
      "builder-protocol-action-container-active-bar",
    ],
  },
  {
    id: "measurementModal",
    label: "Measurement modal",
    tokens: [
      "measurement-modal-filter-button-active-background",
      "measurement-modal-filter-button-active-border",
      "measurement-modal-filter-button-active-text",
      "measurement-modal-filter-button-active-hover-background",
      "measurement-modal-baseline-save-button-text",
      "measurement-modal-baseline-confirm-modal-cancel-button-hover",
    ],
  },
  {
    id: "pickers",
    label: "Date & time pickers",
    tokens: [
      "portal-date-picker-popup-today-cell-border",
      "portal-date-picker-popup-time-cell-selected-background",
      "portal-date-picker-popup-calendar-cell-selected-background",
      "portal-date-picker-popup-ok-button-color",
      "portal-date-picker-popup-ok-button-hover-color",
      "portal-time-picker-hover-border",
      "portal-time-picker-focus-border",
      "portal-time-picker-popup-selected-cell",
      "portal-date-range-day-hover-border",
      "portal-date-range-day-today-text",
      "portal-date-range-day-selected-background",
      "portal-date-range-day-today-background",
      "portal-date-range-trail",
    ],
  },
  {
    id: "inputs",
    label: "Inputs & dropdowns",
    tokens: [
      "portal-toggle-checked-background",
      "portal-toggle-checked-thumb-background",
      "portal-toggle-checked-thumb-border",
      "portal-dropdown-popup-selected-background",
      "portal-dropdown-popup-selected-icon",
      "portal-dropdown-selected-border",
      "portal-text-input-border-hover",
      "portal-text-input-border-focus",
      "portal-radio-button-dot",
    ],
  },
  {
    id: "surveys",
    label: "Surveys",
    tokens: [
      "surveys-progress-bar-stroke-color",
      "surveys-progress-bar-trail-color",
      "surveys-slider-trail",
      "surveys-slider-dot",
    ],
  },
  {
    id: "misc",
    label: "Other portal chrome",
    tokens: [
      "portal-modal-note-background",
      "portal-modal-note-text",
      "portal-rich-text-editor-toolbar-focus-icon",
      "portal-rich-text-editor-toolbar-focus-border",
      "portal-rich-text-editor-toolbar-active-icon",
      "portal-rich-text-editor-toolbar-active-background",
      "care-info-add-link-button-color",
      "care-info-add-link-button-hover-color",
      "patient-details-edit-hover",
      "patient-details-edit-dropdown-hover",
      "portal-loading-state-color",
      "portal-small-add-button-hover",
    ],
  },
];

/**
 * Merges a tenant's saved styles over the defaults — the portal port of
 * Retool's `HandleStyles.js`.
 *
 * Two rules from that script are load-bearing:
 * - a saved key the defaults don't define is **dropped**, so a token retired
 *   from the portal can't linger in a tenant's theme; and
 * - a null or empty saved value falls back to the default rather than
 *   blanking the colour.
 *
 * (Retool gated all of this on `statusCode === 204`, which is inverted: a 204
 * is what the API returns when a tenant has NO saved styles, and it carries no
 * body to parse. Here a 204/absent payload simply means "use the defaults".)
 */
export function mergeTenantStyles(
  savedStyles: Record<string, unknown> | null | undefined
): Record<string, string> {
  const merged: Record<string, string> = { ...DEFAULT_TENANT_STYLES };

  if (!savedStyles) return merged;

  Object.entries(savedStyles).forEach(([token, value]) => {
    if (!Object.prototype.hasOwnProperty.call(merged, token)) return;
    if (value === null || value === undefined || value === "") return;
    merged[token] = String(value);
  });

  return merged;
}

/**
 * Parses the API's `tenantStyles` payload, which is a JSON **string** rather
 * than an object. Returns null when it is absent or unparseable, so the
 * caller falls back to the defaults instead of throwing.
 */
export function parseTenantStyles(
  tenantStyles: string | null | undefined
): Record<string, unknown> | null {
  if (!tenantStyles) return null;

  try {
    const parsed = JSON.parse(tenantStyles);
    return typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** True when every token matches the shipped default. */
export function isDefaultTenantStyles(styles: Record<string, string>): boolean {
  return Object.entries(DEFAULT_TENANT_STYLES).every(
    ([token, value]) => styles[token] === value
  );
}
